/**
=========================================================
* E4A-IoT-Manager React Frontend Dashboard
* Copyright © 2024 Electronics4All Inc.
=========================================================
*/

import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";

// Dropzone
import Dropzone from "dropzone";
import "dropzone/dist/dropzone.css";

// Material Dashboard React components
import MDBox from "components/MDBox";

// Custom styles for the MDDropzone
import MDDropzoneRoot from "components/MDDropzone/MDDropzoneRoot";

// Material Dashboard React context
import { useMaterialUIController } from "context";

// Thumbnail images
import pdfThumbnail from "assets/images/thumbnails/pdf.png";
import docxThumbnail from "assets/images/thumbnails/docx.png";
import xlsThumbnail from "assets/images/thumbnails/xls.png";
import pptxThumbnail from "assets/images/thumbnails/pptx.png";
import stepThumbnail from "assets/images/thumbnails/step.png";

/**
 * MDDropzone
 * the following changes have been to the original component in order to prevent an automatic upload of the file
 * while also preserving the style of the dropzone
 * - File size is now displayed under the thumbnail
 * - The remove button now has an outline
 * - Added custom thumbnails for docx, pdf, pptx, xls and one common thumbnail for other file types
 * - Does NOT auto-upload to a URL. Instead, triggers an "addedfile" event,
 *   which can be capture via onFileAdd to store in your parent form's state.
 * - When ready (e.g., user hits Submit), we can manually upload the file(s)
 *   by reading them from your parent component’s state.
 */
function MDDropzone({ 
  onFileAdd,
  onThunbnailAdd,
  onFileRemove,
  onProcessedFile,
  options 
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  // Ref to the dropzone container
  const dropzoneRef = useRef(null);
 
  const customThumbnails = {
    'application/pdf': pdfThumbnail,
    'application/msword': docxThumbnail,
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': docxThumbnail, 
    'application/vnd.ms-excel': xlsThumbnail,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': xlsThumbnail,
    'application/vnd.ms-powerpoint': pptxThumbnail,
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': pptxThumbnail,
  };
  useEffect(() => {
    Dropzone.autoDiscover = false;

    // Merge in default “no upload” config with any user-specified options
    const dropzoneOptions = {
      url: "#",                // No actual upload URL (placeholder)
      autoProcessQueue: false, // Do not auto-upload
      addRemoveLinks: true,    // Shows the 'Remove file' link
      previewTemplate: `
      <div class="dz-preview dz-file-preview">
        <div class="dz-image"><img data-dz-thumbnail /></div>
        <div class="dz-size"><span data-dz-size></span></div>

       <div class="dz-details"><div class="dz-filename"><span data-dz-name></span></div></div>

        <div class="dz-success-mark"><span>✔</span></div>
        <div class="dz-error-mark" aria-label="Upload failed" role="img">
          <span>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="12" r="11" fill="currentColor" fill-opacity="0.12" stroke="currentColor" stroke-width="1.5" />
              <path d="M8.7 8.7L15.3 15.3M15.3 8.7L8.7 15.3" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
            </svg>
          </span>
        </div>
        <div class="dz-error-message"><span data-dz-errormessage></span></div>
      </div>
      `,
      ...options,
    };
    const dz = new Dropzone(dropzoneRef.current, dropzoneOptions);

    // Listen to selected events and reports them to parent component
    dz.on("addedfile", (file) => {
      if (file.type === ""){
        dz.createThumbnailFromUrl(file, stepThumbnail);
        dz.emit("thumbnail", file, stepThumbnail);
      }else if (customThumbnails[file.type]) {
        dz.createThumbnailFromUrl(file, customThumbnails[file.type]);
        dz.emit("thumbnail", file, customThumbnails[file.type]);
      }
      if (onFileAdd) {
        onFileAdd(file);
      }

    });

    dz.on("thumbnail", (file, dataUrl) => {
      if (onThunbnailAdd) {
        onThunbnailAdd(file);
      }
    });

    dz.on("removedfile", (file) => {
      if (onFileRemove) {
        onFileRemove(file);
      }
    });

    // Cleanup on unmount or re-render
    return () => {
      dz.destroy();
    };
  }, [onFileAdd, options]);

  return (
    <MDDropzoneRoot
      component="form"
      // no "action" attribute needed; we won't auto-upload
      ref={dropzoneRef}
      className="form-control dropzone"
      ownerState={{ darkMode }}
      style={{ maxHeight: '400px', overflowY: 'auto' }} 
    >
      {/**
       * The "fallback" <input> below is part of Dropzone’s default structure:
       * If the user’s browser doesn’t support drag-and-drop, they’ll see this input.
       */}
      <MDBox className="fallback" bgColor="transparent">
        <MDBox component="input" name="file" type="file" multiple />
      </MDBox>
    </MDDropzoneRoot>
  );
}

// Prop Types
MDDropzone.propTypes = {
  /**
   * Callback that fires when a file is added to Dropzone. Receives the single "file" object.
   */
  onFileAdd: PropTypes.func,
  /**
   * Additional Dropzone config overrides. e.g. { acceptedFiles: 'image/*' } 
   */
  options: PropTypes.object,
};

MDDropzone.defaultProps = {
  onFileAdd: null,
  options: {},
};

export default MDDropzone;